import { DeleteOutlined } from '@ant-design/icons';
import { Tooltip, Tag } from 'antd';
import dayjs from 'dayjs';
import { COLOR_TOOLTIP, DATA_EMPTY, DATE_FORMAT } from '@constants/common';
import { TAssetListConvertData } from '@models/asset';
import { TableProps } from 'antd/lib';
import { SortOrder } from 'antd/es/table/interface';
import { formatAssetNo } from '@utils/helpers';
import { ALL_DEVICE_CONSTANT } from './constant';

const { TABLE_HEADER } = ALL_DEVICE_CONSTANT;

const sortDirections: SortOrder[] = ['ascend', 'descend', null];

// Right-aligns only the cell value (header stays centered via column `align`).
const renderRightAligned = (content: React.ReactNode) => (
  <div style={{ textAlign: 'right' }}>{content}</div>
);

// BE returns date-only fields as "YYYY-MM-DD" (DateOnly default); spec 9.14 requires "YYYY/MM/DD" display.
const renderDate = (value?: string) => {
  if (!value) {
    return DATA_EMPTY;
  }
  const formatted = dayjs(value).format(DATE_FORMAT);
  return renderValueTooltip(dayjs(value).isValid() ? formatted : value);
};

const renderValueTooltip = (value?: string) => {
  return value ? (
    <span className="all-device_ellipsis">
      <Tooltip
        color="white"
        overlayClassName="common_custom-tooltip"
        title={value}
        overlayInnerStyle={{
          color: COLOR_TOOLTIP,
        }}
      >
        {value}
      </Tooltip>
    </span>
  ) : (
    DATA_EMPTY
  );
};

const renderStatus = (status?: string) => {
  switch (status) {
    case '未確認':
      return <Tag color="#98a2b3">未確認</Tag>;
    case '確認済':
      return <Tag color="#2e90fa">確認済</Tag>;
    case '未使用':
      return <Tag color="#f79009">未使用</Tag>;
    case '不記載':
      return <Tag color="#f04438">不記載</Tag>;
    case '移動':
      return <Tag color="#9e77ed">移動</Tag>;
    case '除却':
      return <Tag color="#344054">除却</Tag>;
    default:
      return DATA_EMPTY;
  }
};

export const getColumns = (
  _searchParams: URLSearchParams,
  handleRedirectPage: (record: TAssetListConvertData) => void,
  handleRedirectPhoto: (record: TAssetListConvertData) => void,
  handleRequestDelete: (record: TAssetListConvertData) => void,
  _handleSortOrderV2: (val: string) => SortOrder
): TableProps<TAssetListConvertData>['columns'] => {
  return [
    // ── Spec column order (9.1 - 9.20 per VAMS_PC_004_固定資産管理台帳.md) ──

    // [9.1] ステータス — fixed left (cố định đến [9.8] 資産名称 khi cuộn ngang)
    {
      title: TABLE_HEADER.STATUS,
      key: 'status',
      dataIndex: 'status',
      width: 90,
      align: 'center',
      fixed: 'left',
      render: (value) => renderStatus(value),
    },
    // [9.2] 資産番号
    {
      title: TABLE_HEADER.ASSET_NUMBER,
      key: 'assetCode',
      dataIndex: 'assetCode',
      width: 115,
      align: 'center',
      fixed: 'left',
      render: (value) => renderValueTooltip(formatAssetNo(value)),
    },
    // [9.3] 会社 — hiển thị theo cột thô companyOriginCode/companyOriginName (t_assets.CompanyCode/Name),
    // độc lập với 店舗/quan hệ master company.
    {
      title: TABLE_HEADER.COMPANY,
      key: 'companyCode',
      dataIndex: 'companyOriginCode',
      width: 170,
      align: 'center',
      fixed: 'left',
      render: (_value, record) => {
        const text = [record.companyOriginCode, record.companyOriginName]
          .filter(Boolean)
          .join('_');
        return renderValueTooltip(
          text || record.companyCode || record.companyName
        );
      },
    },
    // [9.4] 店舗
    {
      title: TABLE_HEADER.STORE,
      key: 'store',
      dataIndex: 'storeName',
      width: 110,
      align: 'center',
      fixed: 'left',
      render: (_value, record) => {
        const storeText = `${record.storeCode || ''} ${
          record.storeName || ''
        }`.trim();
        return renderValueTooltip(storeText || undefined);
      },
    },
    // [9.5] 種類 — sortable
    {
      title: TABLE_HEADER.CATEGORY,
      key: 'assetType',
      dataIndex: 'assetType',
      sorter: true,
      sortDirections,
      width: 140,
      align: 'center',
      fixed: 'left',
      render: (value) => renderValueTooltip(value),
    },
    // [9.6] 部門 — sortable
    {
      title: TABLE_HEADER.DEPARTMENT,
      key: 'departmentName',
      dataIndex: 'departmentName',
      sorter: true,
      sortDirections,
      width: 100,
      align: 'center',
      fixed: 'left',
      render: (value) => renderValueTooltip(value),
    },
    // [9.7] 所属 — sortable
    {
      title: TABLE_HEADER.AFFILIATION,
      key: 'location',
      dataIndex: 'location',
      sorter: true,
      sortDirections,
      width: 90,
      align: 'center',
      fixed: 'left',
      render: (value) => renderValueTooltip(value),
    },
    // [9.8] 資産名称 — cột rộng nhất, giữ đủ chỗ cho tên dài (ellipsis + tooltip); cột cuối cùng được fixed left
    {
      title: TABLE_HEADER.ASSET_NAME,
      key: 'assetName',
      dataIndex: 'assetName',
      width: 220,
      align: 'center',
      fixed: 'left',
      render: (value, record) => {
        return value ? (
          <span
            onClick={() => handleRedirectPage(record)}
            className="all-device_ellipsis all-device_link"
          >
            <Tooltip
              color="white"
              overlayClassName="common_custom-tooltip"
              title={value}
              overlayInnerStyle={{
                color: COLOR_TOOLTIP,
              }}
            >
              {value}
            </Tooltip>
          </span>
        ) : (
          DATA_EMPTY
        );
      },
    },
    // [9.9] 数量 — value Right-aligned (header stays centered)
    {
      title: TABLE_HEADER.QUANTITY,
      key: 'quantity',
      dataIndex: 'quantity',
      width: 70,
      align: 'center',
      render: (value: number) =>
        renderRightAligned(Number.isFinite(value) ? value : DATA_EMPTY),
    },
    // [9.10] 取得年月日
    {
      title: TABLE_HEADER.ACQUISITION_DATE,
      key: 'purchaseDate',
      dataIndex: 'purchaseDate',
      width: 110,
      align: 'center',
      render: (value) => renderDate(value),
    },
    // [9.11] 耐用年数 — sortable; value Right-aligned (header stays centered)
    {
      title: TABLE_HEADER.USEFUL_LIFE,
      key: 'usefulLife',
      dataIndex: 'usefulLife',
      sorter: true,
      sortDirections,
      width: 90,
      align: 'center',
      render: (value) => renderRightAligned(renderValueTooltip(value)),
    },
    // [9.12] 取得価額 — sortable; value Right-aligned (header stays centered)
    {
      title: TABLE_HEADER.ACQUISITION_COST,
      key: 'purchasePrice',
      dataIndex: 'purchasePrice',
      sorter: true,
      sortDirections,
      width: 120,
      align: 'center',
      render: (value?: number) =>
        renderRightAligned(
          Number.isFinite(value) ? value?.toLocaleString('ja-JP') : DATA_EMPTY
        ),
    },
    // [9.13] 期首帳簿価額 — sortable; value Right-aligned (header stays centered)
    {
      title: TABLE_HEADER.OPENING_BOOK_VALUE,
      key: 'bookValueStart',
      dataIndex: 'bookValueStart',
      sorter: true,
      sortDirections,
      width: 130,
      align: 'center',
      render: (value?: number) =>
        renderRightAligned(
          Number.isFinite(value) ? value?.toLocaleString('ja-JP') : DATA_EMPTY
        ),
    },
    // [9.14] 最新年月日 — sortable
    {
      title: TABLE_HEADER.LAST_UPDATED_DATE,
      key: 'latestUpdatedAt',
      dataIndex: 'latestUpdatedAt',
      sorter: true,
      sortDirections,
      width: 110,
      align: 'center',
      render: (value) => renderDate(value),
    },
    // [9.15] 写真有無 — sortable
    {
      title: TABLE_HEADER.PHOTO,
      key: 'hasPhoto',
      dataIndex: 'hasPhoto',
      sorter: true,
      sortDirections,
      width: 80,
      align: 'center',
      render: (value, record) =>
        value ? (
          <span
            className="all-device_action-icon"
            onClick={() => handleRedirectPhoto(record)}
          >
            ✓
          </span>
        ) : (
          DATA_EMPTY
        ),
    },
    // [9.16] 備考
    {
      title: TABLE_HEADER.REMARKS,
      key: 'remarks',
      dataIndex: 'remarks',
      width: 160,
      align: 'center',
      render: (value) => renderValueTooltip(value),
    },
    // [9.17] タグ要求
    {
      title: TABLE_HEADER.STICKER_REQUEST,
      key: 'sealRequestQuantity',
      dataIndex: 'sealRequestQuantity',
      width: 100,
      align: 'center',
      render: (value) => renderValueTooltip(value),
    },
    // [9.20] アクション (削除) — camera/edit đã có link ở [9.15] 写真有無 và [9.8] 資産名称, không lặp lại ở đây.
    {
      title: 'アクション',
      key: 'actionColumn',
      dataIndex: 'actionColumn',
      width: 90,
      align: 'center',
      render: (_value, record) =>
        record.canDelete ? (
          <span
            className="all-device_action-icon all-device_action-icon-danger"
            onClick={() => handleRequestDelete(record)}
          >
            <DeleteOutlined />
          </span>
        ) : (
          DATA_EMPTY
        ),
    },
  ];
};
